// Models
import { ChildCollectionPermissions, EntityCollectionPermissions, MovePlanPermissions, MoveTaskPermissions, PriorityPermissions } from "../client/permissions/Enumerations";
/** Default attachment permissions to user when data is not available. */ export const DefaultAttachmentPermissions = {
    canDelete: true,
    canEdit: true
};
/** The most restrictive attachment permissions. */ export const RestrictedAttachmentPermissions = {
    canDelete: false,
    canEdit: false
};
/** Default bucket permissions to use when data is not available. */ export const DefaultBucketPermissions = {
    canDelete: true,
    canReorder: true,
    canUpdateDisplayName: true,
    canUpdateColor: false
};
/** The most restrictive bucket permissions. */ export const RestrictedBucketPermissions = {
    canDelete: false,
    canReorder: false,
    canUpdateDisplayName: false,
    canUpdateColor: false
};
/** The most restrictive custom field definition permissions. */ export const RestrictedCustomFieldDefinitionPermissions = {
    canDelete: false,
    customFieldChoicePermissions: ChildCollectionPermissions.EnableNone,
    canUpdateDisplayName: false,
    canUpdateRollup: false
};
/** The most permissive custom field definition permissions. */ export const PermissiveCustomFieldDefinitionPermissions = {
    canDelete: true,
    customFieldChoicePermissions: ChildCollectionPermissions.EnableAll,
    canUpdateDisplayName: true,
    canUpdateRollup: true
};
/**
 * Default custom field definition permissions to use when data is not available.
 * Using the most restrictive permissions by default for custom field definitions since they are gated on premium licensing
 */ export const DefaultCustomFieldDefinitionPermissions = RestrictedCustomFieldDefinitionPermissions;
/** The most restrictive dependency permissions. */ export const RestrictedDependencyPermissions = {
    canDelete: false,
    canUpdateConstraint: false
};
/** Default dependency permissions to use when data is not available. */ export const DefaultDependencyPermissions = {
    canDelete: false,
    canUpdateConstraint: false
};
/** Restricted checklist item permissions to use when data is not available. */ export const RestrictedChecklistItemPermissions = {
    canDelete: false,
    canToggleCheck: false,
    canUpdateDisplayName: false,
    canReorder: false,
    canPromote: false
};
/** Default checklist item permissions to use when data is not available. */ export const DefaultChecklistItemPermissions = {
    canDelete: true,
    canToggleCheck: true,
    canUpdateDisplayName: true,
    canReorder: true,
    canPromote: true
};
/** Default container permissions to use when data is not available. */ export const DefaultContainerPermissions = {
    canAddMembers: false,
    canRemoveMembers: false,
    canViewMembers: true,
    canChangeSensitivity: true,
    canUseAgents: false,
    canUploadFiles: false,
    canUseExchangeBasedCommentThreads: false,
    canUseTaskChat: false,
    canUseGoals: false
};
/** Default goals permissions to use when data is not available. */ export const DefaultGoalsPermissions = {
    canDelete: true,
    canUpdateDisplayName: true,
    canUpdatePriority: true,
    canUpdateFinishDate: true,
    canUpdateNotes: true,
    canUpdateStartDate: true,
    canUpdateStatus: true
};
/** The most restrictive goals permissions. */ export const RestrictedGoalsPermissions = {
    canDelete: false,
    canUpdateDisplayName: false,
    canUpdatePriority: false,
    canUpdateFinishDate: false,
    canUpdateNotes: false,
    canUpdateStartDate: false,
    canUpdateStatus: false
};
/** Default plan permissions to use when data is not available. */ export const DefaultPlanPermissions = {
    bucketPermissions: EntityCollectionPermissions.EnableAll,
    goalPermissions: EntityCollectionPermissions.EnableNone,
    taskPermissions: EntityCollectionPermissions.EnableAll,
    movePlanPermissions: MovePlanPermissions.EnableAll,
    canCopy: true,
    canShare: true,
    canDelete: true,
    canUpdateCategories: true,
    canUpdateDisplayName: true,
    canUseAgents: false,
    canAssignAgents: false,
    canUpdateVisuals: true,
    canExport: true,
    canPrint: true,
    customFieldDefinitionPermissions: EntityCollectionPermissions.EnableNone,
    dependencyPermissions: EntityCollectionPermissions.EnableNone,
    canUpdateConditionalColoringRules: false,
    canUpdateCalendar: true,
    canUpdatePlanExpiry: false
};
/** The most restrictive plan permissions. */ export const RestrictedPlanPermissions = {
    bucketPermissions: EntityCollectionPermissions.EnableNone,
    goalPermissions: EntityCollectionPermissions.EnableNone,
    taskPermissions: EntityCollectionPermissions.EnableNone,
    movePlanPermissions: MovePlanPermissions.EnableNone,
    canCopy: false,
    canShare: false,
    canDelete: false,
    canUpdateCategories: false,
    canUpdateDisplayName: false,
    canUseAgents: false,
    canAssignAgents: false,
    canUpdateVisuals: false,
    canExport: false,
    canPrint: false,
    customFieldDefinitionPermissions: EntityCollectionPermissions.EnableNone,
    dependencyPermissions: EntityCollectionPermissions.EnableNone,
    canUpdateConditionalColoringRules: false,
    canUpdateCalendar: false,
    canUpdatePlanExpiry: false
};
/** Default task permissions to use when data is not available. */ export const DefaultTaskPermissions = {
    canEditAssignments: true,
    attachmentPermissions: EntityCollectionPermissions.EnableAll,
    checklistPermissions: EntityCollectionPermissions.EnableAll,
    moveTaskPermissions: MoveTaskPermissions.EnableAll,
    canUpdateStatus: true,
    canUpdatePercentComplete: true,
    priorityPermissions: PriorityPermissions.EnableAll,
    canComment: true,
    canCopy: true,
    canDelete: true,
    canReorder: true,
    canUpdateAppliedLabels: true,
    canUpdateDisplayName: true,
    canUpdateDueDateTime: true,
    canUpdateFinishDateTime: false,
    canUpdateNotes: true,
    canUpdatePreviewType: true,
    canUpdateRecurrence: true,
    canUpdateStartDateTime: true,
    customFieldValuePermissions: ChildCollectionPermissions.EnableNone,
    canUpdateParentTask: false,
    canUpdateGoalIds: false
};
/** The most restrictive task permissions. */ export const RestrictedTaskPermissions = {
    canEditAssignments: false,
    attachmentPermissions: EntityCollectionPermissions.EnableNone,
    checklistPermissions: EntityCollectionPermissions.EnableNone,
    moveTaskPermissions: MoveTaskPermissions.EnableNone,
    canUpdateStatus: false,
    canUpdatePercentComplete: false,
    priorityPermissions: PriorityPermissions.EnableNone,
    canComment: false,
    canCopy: false,
    canDelete: false,
    canReorder: false,
    canUpdateAppliedLabels: false,
    canUpdateDisplayName: false,
    canUpdateDueDateTime: false,
    canUpdateFinishDateTime: false,
    canUpdateNotes: false,
    canUpdatePreviewType: false,
    canUpdateRecurrence: false,
    canUpdateStartDateTime: false,
    customFieldValuePermissions: ChildCollectionPermissions.EnableNone,
    canUpdateParentTask: false,
    canUpdateGoalIds: false
};
/** Default user permissions to use when data is not available. */ export const DefaultUserPermissions = {
    canAddGuests: false,
    canCreateGroup: false,
    canUseCopilot: false,
    canAccessGoals: false,
    canUseExchangeDependentFeatures: false,
    canAccessTimelineView: false,
    canAccessTeamView: false,
    canAccessPeopleView: false,
    canViewTaskHistory: false,
    canViewCriticalPath: false,
    canAccessSprints: false,
    canCreatePortfolios: false,
    canAccessFinishDate: false,
    canCreateLegacyPremiumPlan: false,
    canCreateLegacyPremiumPlanAsRestrictedUser: false,
    canReadLegacyPremiumPlans: false,
    canAccessRoadmap: false
};
